import styled, {css} from 'styled-components';
import {DayStyleProps, HOVER_TYPE_ENUM} from './Day.types';

const DayTooltip = styled.div<DayStyleProps>`
	width: 71px;
	height: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 4px;
	position: absolute;
	z-index: ${({theme}) => theme.zIndex.tooltip};
	top: -28px;
	box-shadow: 0px 12px 12px rgba(0, 0, 0, 0.04);

	background: #f9f9f9;

	opacity: 0;
	visibility: hidden;

	transition: 0.3s;

	${({theme}) => css`
		border: 0.3px solid ${theme.colors.gray['30Percent']};
	`}

	${({isSaturday}) =>
		isSaturday &&
		css`
			left: -25px;
		`}

    ${({isSunday}) =>
		isSunday &&
		css`
			left: 4px;
		`}
`;
const DayTooltipLabel = styled.span`
	line-height: 22px;
`;

const DayWrapper = styled.button<DayStyleProps>`
	border: 0;
	background: transparent;
	cursor: pointer;

	position: relative;

	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 2px;

	&:disabled {
		cursor: not-allowed;
		& div {
			background-color: transparent;
		}
	}

	&:hover {
		& > div:first-child {
			opacity: 1;
			visibility: visible;
			top: -32px;
		}
	}

	${({theme}) => theme.breakpoints.up('sm')} {
		width: 48px;
		height: 48px;
	}

	${({hoverType, isDaySelected, theme, hasColorPinDay}) =>
		hoverType === HOVER_TYPE_ENUM.COLOR &&
		!isDaySelected &&
		!hasColorPinDay &&
		css`
			transition: color 0.3s;
			&:hover:not(:disabled) {
				span {
					color: ${theme.colors.brand.primary.blue};
				}
			}
		`}

	${({hoverType, isDaySelected, theme}) =>
		hoverType === HOVER_TYPE_ENUM.CIRCLE &&
		!isDaySelected &&
		css`
			&:hover:not(:disabled) {
				&::before {
					content: '';
					width: 40px;
					height: 40px;
					background-color: #e0eaf6;
					border: 2px solid ${theme.colors.brand.primary.blue};
					border-radius: 8px;
					position: absolute;

					${theme.breakpoints.up('sm')} {
						width: 48px;
						height: 48px;
					}
				}
			}
		`}

    ${({showBackgroundIntervalWhenHover, hasFirstDaySelected, hasSecondDaySelected, theme}) =>
		showBackgroundIntervalWhenHover &&
		hasFirstDaySelected &&
		!hasSecondDaySelected &&
		css`
			&:hover:not(:disabled) {
				&::before {
					content: '';
					width: 40px;
					height: 40px;
					left: 0;
					border-radius: 0 8px 8px 0;
					margin-right: 0px;
					position: absolute;

					${theme.breakpoints.up('sm')} {
						width: 48px;
						height: 48px;
					}
				}

				&::after {
					content: '';
					width: 40px;
					height: 40px;
					border: 2px solid ${theme.colors.brand.primary.blue};
					border-radius: 8px;
					position: absolute;

					${theme.breakpoints.up('sm')} {
						width: 48px;
						height: 48px;
					}
				}
			}
		`}

		${({theme}) => css`
		&:focus {
			border-radius: 8px;
			outline: 2px solid rgba(2, 108, 182, 0.2);
			border: 2px solid ${theme.colors.brand.primary.blue};

			${theme.breakpoints.up('sm')} {
				outline: 4px solid rgba(2, 108, 182, 0.2);
			}

			&::after {
				content: '';
				width: 40px;
				height: 40px;
				border: 2px solid ${theme.colors.brand.primary.blue};
				border-radius: 8px;
				position: absolute;

				${theme.breakpoints.up('sm')} {
					width: 48px;
					height: 48px;
				}
			}
		}
	`}
`;

const DayContent = styled.div<DayStyleProps>`
	width: 40px;
	height: 40px;

	display: flex;
	align-items: center;
	justify-content: center;
	background-color: #e0eaf6;
	border-radius: 8px;

	overflow: hidden;

	${({theme}) => theme.breakpoints.up('sm')} {
		width: 48px;
		height: 48px;
	}

	${({isDisableColor}) =>
		isDisableColor &&
		css`
			background-color: transparent;
		`}

	${({setBackgroundInterval, theme}) =>
		setBackgroundInterval &&
		css`
			&::before {
				content: '';
				display: block;
				width: 54px;
				height: 40px;
				background: #e0eaf6;
				border-top: 2px solid ${theme.colors.brand.primary.blue};
				border-bottom: 2px solid ${theme.colors.brand.primary.blue};
				position: absolute;
				z-index: ${theme.zIndex.tooltip};

				${theme.breakpoints.up('sm')} {
					width: 64px;
					height: 48px;
				}
			}
		`}

	${({isSunday}) =>
		isSunday &&
		css`
			&::before {
				border-radius: 0px;
			}
		`}

  ${({isSaturday}) =>
		isSaturday &&
		css`
			&::before {
				border-radius: 0px;
			}
		`}
  
  ${({isFirstDaySelected, theme}) =>
		isFirstDaySelected &&
		css`
			&::before {
				width: calc(40px - 10px);
				left: 10px;
				border-radius: 8px 0 0 8px;
				background-color: ${theme.colors.brand.primary.blue};

				${theme.breakpoints.up('sm')} {
					width: calc(48px - 10px);
				}
			}
		`}

  ${({isFirstDayOfMonth}) =>
		isFirstDayOfMonth &&
		css`
			&::before {
				border-radius: 0px;
			}
		`}

  ${({isFirstDayOfMonth, isDaySelected, isSaturday}) =>
		isFirstDayOfMonth &&
		isDaySelected &&
		isSaturday &&
		css`
			&::before {
				display: none;
			}
		`}

    ${({isLastDayOfMonth, isDaySelected, isFirstDaySelected}) =>
		isLastDayOfMonth &&
		isDaySelected &&
		isFirstDaySelected &&
		css`
			&::before {
				display: none;
			}
		`}

  ${({theme, isFirstDayOfMonth, isSaturday}) =>
		isFirstDayOfMonth &&
		isSaturday &&
		css`
			&::before {
				width: 40px;
				height: 40px;
				border-radius: 0px;

				${theme.breakpoints.up('sm')} {
					width: 48px;
					height: 48px;
				}
			}
		`}

  ${({isFirstDaySelected, isSaturday}) =>
		isFirstDaySelected &&
		isSaturday &&
		css`
			&::before {
				display: none;
			}
		`}

  ${({isSecondDaySelected, isSunday}) =>
		isSecondDaySelected &&
		isSunday &&
		css`
			&::before {
				display: none;
			}
		`}

    ${({isSecondDaySelected, isFirstDayOfMonth}) =>
		isSecondDaySelected &&
		isFirstDayOfMonth &&
		css`
			&::before {
				display: none;
			}
		`}
  
  ${({isSecondDaySelected, theme}) =>
		isSecondDaySelected &&
		css`
			&::before {
				width: calc(40px - 10px);
				right: 10px;
				border-radius: 0 8px 8px 0;
				background-color: ${theme.colors.brand.primary.blue};

				${theme.breakpoints.up('sm')} {
					width: calc(48px - 10px);
				}
			}
		`}

  ${({isFirstDaySelected, isSecondDaySelected}) =>
		isFirstDaySelected &&
		isSecondDaySelected &&
		css`
			&::before {
				display: none;
			}
		`}

  ${({isLastDayOfMonth}) =>
		isLastDayOfMonth &&
		css`
			&::before {
				border-radius: 0px;
			}
		`}

  ${({theme, isLastDayOfMonth, isSunday}) =>
		isLastDayOfMonth &&
		isSunday &&
		css`
			&::before {
				width: 40px;
				height: 40px;
				border-radius: 0px;

				${theme.breakpoints.up('sm')} {
					width: 48px;
					height: 48px;
				}
			}
		`}

  ${({isDaySelected, theme}) =>
		isDaySelected &&
		css`
			&::after {
				content: '';
				width: 40px;
				height: 40px;
				background-color: ${theme.colors.brand.primary.blue};
				position: absolute;
				border-radius: 8px;

				${theme.breakpoints.up('sm')} {
					width: 48px;
					height: 48px;
				}
			}
		`}
`;

const DayLabel = styled.span`
	font-size: 16px;
	z-index: ${({theme}) => theme.zIndex.tooltip};
`;

const TodayIndicator = styled.div`
	width: 4px;
	height: 4px;
	border-radius: 4px;
	background-color: ${({theme}) => theme.colors.brand.primary.blue};
	margin: 0 auto;
`;

const IconPinContainer = styled.div`
	position: absolute;
	top: 0;
	right: 0;
`;

export {
	DayTooltip,
	DayTooltipLabel,
	DayWrapper,
	DayContent,
	DayLabel,
	TodayIndicator,
	IconPinContainer,
};
